// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Button,
  Column,
  Description,
  InlineAlert,
  LgPadding,
  SmSpacing,
  Toast,
  ToastContext,
} from '@alto/design-system';
import { type ApiResponseRow } from '@alto/design-system/src/components/inputs/src/InputAutocomplete/types';
import {
  AllergiesInput,
  MedicalConditionsInput,
  NO_KNOWN_ALLERGIES,
  NO_KNOWN_MEDICAL_CONDITIONS,
  getAllergensForInput,
} from '@alto/features';
import React, { useContext } from 'react';
import { useController, useForm } from 'react-hook-form';
// eslint-disable-next-line import/no-deprecated
import { clearUserError, updateUser } from '~shared/actions/users';
import getSelectedUser from '~shared/features/users/selectors/getSelectedUser';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { type AsyncValue, formatAsyncSelectInitialValue } from '~shared/helpers/formatAsyncSelectValues';
import getUserError from '~shared/selectors/ui/errors/getUserError';
import { useDispatchShared, useSelectorShared } from '~shared/store';

type Values = {
  allergies?: string;
  allergens: string[];
  hasAllergens: boolean;
  medical_conditions: string;
};

export const MedicalInfoActionSheet = () => {
  const dispatch = useDispatchShared();
  const { addToast } = useContext(ToastContext);
  const { closeActionSheet } = useContext(ActionSheetContext);
  const userError = useSelectorShared(getUserError);
  const loading = useSelectorShared((state) => state.ui.loading.updateUserLoading);
  const currentUserID = useSelectorShared(getCurrentUserID);
  const user = useSelectorShared(getSelectedUser);
  const firstName = currentUserID !== user?.id ? (user?.preferred_first_name ?? user?.first_name ?? '') : '';

  const allergens = getAllergensForInput(user);
  const {
    control,
    formState: { submitCount },
    getValues,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      allergens,
      hasAllergens: allergens.length > 0,
      medical_conditions: user?.medical_conditions || NO_KNOWN_MEDICAL_CONDITIONS,
      onboarding_other_scripts: user?.onboarding_other_scripts,
      other_scripts_list: formatAsyncSelectInitialValue(user?.other_scripts_list ?? []) as AsyncValue[],
    },
  });

  // the BE handles parsing free-form text allergies and structured allergens
  // submit all allergies through allergens field
  const allergensController = useController({
    control,
    name: 'allergens',
    rules: {
      validate: (value) => {
        return getValues('hasAllergens') && value.length === 0 ? 'Allergies cannot be blank' : true;
      },
    },
  });
  // controller to track yes/no allergies selection for error reporting
  const hasAllergensController = useController({
    control,
    name: 'hasAllergens',
  });

  const medicalConditionsController = useController({
    control,
    name: 'medical_conditions',
    rules: { required: 'Medical conditions cannot be blank', minLength: 1 },
  });

  // Prevent modal from rendering until user has loaded,
  // so defaultValues / reset align with the loaded user
  if (!user) return null;

  const handleSaveMedicalInfo = async (values: Values) => {
    if (values.allergens.length === 0) {
      values.allergens = [NO_KNOWN_ALLERGIES];
    } else {
      values.allergies = '';
    }
    // eslint-disable-next-line import/no-deprecated
    const params: Parameters<typeof updateUser>[0] = {
      id: user.id,
      ...values,
    };
    // eslint-disable-next-line import/no-deprecated
    const { success } = await dispatch(updateUser(params));
    if (success) {
      dispatch(clearUserError());
      closeActionSheet();
      addToast(<Toast>Updated medical info</Toast>);
    }
  };

  const handleAllergiesChange = (allergies: ApiResponseRow<string>[]) => {
    const value = allergies.map((allergy) => allergy.value);
    allergensController.field.onChange(value);
  };

  const handleClose = () => {
    reset();
    dispatch(clearUserError());
    closeActionSheet();
  };

  return (
    <ActionSheetV2
      handleClose={handleClose}
      title={firstName ? `Edit medical info (for ${firstName})` : 'Edit medical info'}
      buttons={[
        <Button
          key="save-medical-info-button"
          label="Save medical info"
          loading={loading}
          loadingLabel="Saving medical info"
          onPress={handleSubmit(handleSaveMedicalInfo)}
        />,
        <Button
          key="dismiss-medical-info-button"
          label="Cancel"
          type="tertiary"
          onPress={handleClose}
        />,
      ]}
    >
      <Column gap="SM">
        {userError ? (
          <LgPadding
            topPadding={SPACING.STATIC.NONE}
            bottomPadding={SPACING.STATIC.NONE}
          >
            <InlineAlert>
              <Description>{userError.message}</Description>
            </InlineAlert>
            <SmSpacing />
          </LgPadding>
        ) : undefined}
        <AllergiesInput
          allergies={allergensController.field.value ?? undefined}
          error={allergensController.fieldState.error?.message}
          isTouched={allergensController.fieldState.isTouched}
          handleChange={handleAllergiesChange}
          handleButtonChange={hasAllergensController.field.onChange}
          hasSubmitted={submitCount > 0}
        />
        <MedicalConditionsInput
          medicalConditions={medicalConditionsController.field.value}
          handleChange={medicalConditionsController.field.onChange}
          error={medicalConditionsController.fieldState.error?.message}
          isTouched={medicalConditionsController.fieldState.isTouched}
          hasSubmitted={submitCount > 0}
        />
      </Column>
    </ActionSheetV2>
  );
};
